import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, isSessionError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_CLS, ROLE_LABEL, emptyPermissions } from "@/lib/staff";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DataTab, AccessTab, PasswordTab, RoleTab } from "@/pages/admin/StaffTabs";

const CreateDialog = ({ groups, onClose, onCreated }) => {
  const [form, setForm] = useState({ username: "", name: "", password: "", role: "anggota", group_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/staff/users", {
        ...form,
        group_id: form.group_id || null,
        permission_mode: form.group_id ? "group" : "custom",
        permissions: emptyPermissions(),
      });
      toast.success("Akun dibuat. Semua menu OFF sampai Anda memberi akses.");
      onCreated();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0f0f14] text-white sm:max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Buat Akun Baru</DialogTitle>
          <DialogDescription className="text-zinc-500">Username dipakai untuk login.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input value={form.name} onChange={set("name")} placeholder="Nama lengkap" className="rk-input" data-testid="create-name" required />
          <input value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toLowerCase() }))} placeholder="Username (huruf kecil/angka)" className="rk-input" data-testid="create-username" required />
          <input value={form.password} onChange={set("password")} placeholder="Password (min. 6 karakter)" className="rk-input" data-testid="create-password" required />
          <select value={form.role} onChange={set("role")} className="rk-input" data-testid="create-role">
            <option value="anggota">ANGGOTA</option>
            <option value="kapten">KAPTEN</option>
          </select>
          <select value={form.group_id} onChange={set("group_id")} className="rk-input" data-testid="create-group">
            <option value="">— Tanpa bagian —</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          <button type="submit" disabled={busy} className="rk-btn-primary w-full text-sm" data-testid="create-user-submit">
            {busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Buat Akun"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditDialog = ({ target, groups, members, onClose, onSaved }) => {
  const { user } = useAuth();
  const isMaster = user.role === "master";
  const powers = user.kapten?.powers || {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#0f0f14] text-white sm:max-w-xl" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle>{target.name} <span className="font-mono text-sm text-zinc-500">@{target.username}</span></DialogTitle>
          <DialogDescription className="text-zinc-500">Perubahan langsung berlaku dan tercatat di riwayat aktivitas.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="access">
          <TabsList className="flex flex-wrap bg-black/50">
            <TabsTrigger value="access" data-testid="tab-access">Hak Akses</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data">Data</TabsTrigger>
            <TabsTrigger value="password" data-testid="tab-password">Password</TabsTrigger>
            {isMaster ? <TabsTrigger value="role" data-testid="tab-role">Jabatan & Wewenang</TabsTrigger> : null}
          </TabsList>
          <TabsContent value="access" className="pt-3">
            <AccessTab target={target} groups={groups} canEdit={isMaster || powers.set_permissions} onSaved={onSaved} />
          </TabsContent>
          <TabsContent value="data" className="pt-3">
            <DataTab target={target} groups={groups} canEdit={isMaster || powers.edit_members} onSaved={onSaved} />
          </TabsContent>
          <TabsContent value="password" className="pt-3">
            <PasswordTab target={target} canEdit={isMaster || powers.reset_password} onSaved={onSaved} />
          </TabsContent>
          {isMaster ? (
            <TabsContent value="role" className="pt-3">
              <RoleTab target={target} groups={groups} members={members} onSaved={onSaved} />
            </TabsContent>
          ) : null}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, groups, canToggle, onToggle, onEdit }) => (
  <tr className="border-t border-white/5 bg-[#0d0d12]" data-testid={`user-row-${u.username}`}>
    <td className="px-4 py-3">
      <div className="font-medium">{u.name}</div>
      <div className="font-mono text-xs text-zinc-500">@{u.username}</div>
    </td>
    <td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-[10px] font-bold ${ROLE_CLS[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
    <td className="px-4 py-3 text-zinc-400">{groups.find((g) => g.id === u.group_id)?.name || "—"}</td>
    <td className="px-4 py-3">
      {u.role === "master" ? (
        <span className="text-xs text-emerald-400">Aktif</span>
      ) : (
        <div className="flex items-center gap-2">
          <Switch checked={u.is_active} disabled={!canToggle} onCheckedChange={(on) => onToggle(u, on)} data-testid={`status-switch-${u.username}`} />
          <span className={`text-xs ${u.is_active ? "text-emerald-400" : "text-rose-400"}`}>{u.is_active ? "Aktif" : "Nonaktif"}</span>
        </div>
      )}
    </td>
    <td className="px-4 py-3 text-right">
      {u.role === "master" ? (
        <span className="text-xs text-zinc-600">Terkunci</span>
      ) : (
        <button onClick={() => onEdit(u)} className="rk-btn-ghost inline-flex items-center gap-1 text-xs" data-testid={`edit-user-${u.username}`}>
          <Pencil size={12} /> Atur
        </button>
      )}
    </td>
  </tr>
);

export const StaffPanel = () => {
  const { user } = useAuth();
  const isMaster = user.role === "master";
  const canToggle = isMaster || user.kapten?.powers?.toggle_status;
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, g] = await Promise.all([api.get("/staff/users"), api.get("/staff/groups")]);
      setUsers(u.data.items);
      setGroups(g.data.items);
    } catch (e) {
      if (!isSessionError(e)) toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (u, on) => {
    try {
      await api.post(`/staff/users/${u.id}/status`, { is_active: on });
      toast.success(on ? `${u.username} diaktifkan` : `${u.username} dinonaktifkan — langsung keluar dari sistem`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const editing = users.find((u) => u.id === editingId);
  const members = users.filter((u) => u.role === "anggota");

  return (
    <section data-testid="staff-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-400">
          {isMaster ? "Kelola semua akun KAPTEN & ANGGOTA." : "Anggota yang berada di bawah pengawasan Anda."}
        </p>
        {isMaster ? (
          <button onClick={() => setCreating(true)} className="rk-btn-primary inline-flex items-center gap-1 text-sm" data-testid="create-user-btn">
            <Plus size={14} /> Buat Akun
          </button>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/5">
        <table className="w-full text-sm">
          <thead className="bg-[#13131a] text-[11px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Akun</th>
              <th className="px-4 py-3 text-left">Jabatan</th>
              <th className="px-4 py-3 text-left">Bagian</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-zinc-500">Belum ada akun.</td></tr> : null}
            {users.map((u) => (
              <UserRow key={u.id} u={u} groups={groups} canToggle={canToggle} onToggle={toggle} onEdit={(x) => setEditingId(x.id)} />
            ))}
          </tbody>
        </table>
      </div>
      {creating ? <CreateDialog groups={groups} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} /> : null}
      {editing ? <EditDialog key={editing.id} target={editing} groups={groups} members={members} onClose={() => setEditingId(null)} onSaved={load} /> : null}
    </section>
  );
};
